import { useCallback, useState } from 'react';
import { walletApi } from '../../../services/api/wallet';
import { pickLatestWithdrawalId } from '../utils/walletWithdrawalsUtils';

export interface UseWalletWithdrawalSuccessOptions {
  influencerId?: string;
  onRefreshBalance: () => void;
  onRefreshWithdrawals: () => void;
  /** Fallback when payout detail cannot open — e.g. scroll to withdrawals list. */
  onViewWithdrawalsFallback?: () => void;
}

/**
 * After Whop confirms a withdrawal, show success modal and route "View status"
 * to the Foodfluence payout detail view.
 */
export function useWalletWithdrawalSuccess({
  influencerId,
  onRefreshBalance,
  onRefreshWithdrawals,
  onViewWithdrawalsFallback,
}: UseWalletWithdrawalSuccessOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const [latestWithdrawalId, setLatestWithdrawalId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleWithdrawalComplete = useCallback(async () => {
    onRefreshBalance();
    onRefreshWithdrawals();

    let withdrawalId: string | null = null;
    if (influencerId) {
      try {
        const { withdrawals } = await walletApi.getWithdrawals({ influencerId });
        withdrawalId = pickLatestWithdrawalId(withdrawals);
      } catch {
        withdrawalId = null;
      }
    }

    setLatestWithdrawalId(withdrawalId);
    setIsOpen(true);
  }, [influencerId, onRefreshBalance, onRefreshWithdrawals]);

  const handleViewStatus = useCallback(() => {
    setIsOpen(false);
    if (latestWithdrawalId) {
      setDetailOpen(true);
      return;
    }
    onViewWithdrawalsFallback?.();
  }, [latestWithdrawalId, onViewWithdrawalsFallback]);

  const handleCloseSuccessModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setDetailOpen(false);
  }, []);

  return {
    isSuccessModalOpen: isOpen,
    isDetailModalOpen: detailOpen,
    latestWithdrawalId,
    handleWithdrawalComplete,
    handleViewStatus,
    handleCloseSuccessModal,
    handleCloseDetailModal,
  };
}
